import { isString } from 'lodash-es';

import { isNodeRef } from '../view/utils/node-ref';

import { DIRECT_RELATION_FILTER_VALUE_SEPARATOR } from './consts';
import type {
  NodeRef,
  NodeWithProperties,
  ObservationViewProperty,
  PropertyRecord,
} from './types';
import { isDirectRelation, isFilesProperty } from './utils';

type EnrichedNodeRef = NodeRef & {
  name: string;
};

export const getNodeRefKey = (ref: { space: string; externalId: string }) =>
  `${ref.space}:${ref.externalId}`;

export const getDirectRelationFilterValue = (ref: {
  space: string;
  externalId: string;
}): string =>
  `${ref.space}${DIRECT_RELATION_FILTER_VALUE_SEPARATOR}${ref.externalId}`;

type DirectRelationWithOptionalName = NodeRef & {
  name?: string;
};

const isDirectRelationWithOptionalName = (
  value: unknown
): value is DirectRelationWithOptionalName => isNodeRef(value);

export const isDirectRelationRef = (value: unknown): value is NodeRef =>
  isNodeRef(value) && Object.keys(value).length === 2;

export const getDirectRelationPropertiesForEnrichment = (
  viewProperties: ObservationViewProperty[]
): ObservationViewProperty[] =>
  viewProperties.filter(
    (property) => isDirectRelation(property) && !isFilesProperty(property)
  );

export const collectDirectRelationRefsForProperty = (
  originalNodes: PropertyRecord[],
  propertyId: string
): NodeRef[] => {
  const seen = new Set<string>();
  const directRelationRefs: NodeRef[] = [];

  const addRef = (ref: NodeRef | undefined) => {
    if (!ref?.space || !ref.externalId) {
      return;
    }
    const key = getNodeRefKey(ref);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    directRelationRefs.push({ space: ref.space, externalId: ref.externalId });
  };

  originalNodes.forEach((originalNode) => {
    const value = originalNode[propertyId];

    if (Array.isArray(value)) {
      value.forEach((item: unknown) => {
        if (isDirectRelationRef(item)) {
          addRef(item);
        }
      });
    } else if (isDirectRelationRef(value)) {
      addRef(value);
    }
  });

  return directRelationRefs;
};

export const sortDirectRelationRefs = (refs: NodeRef[]): NodeRef[] =>
  [...refs].sort((refA, refB) =>
    getNodeRefKey(refA).localeCompare(getNodeRefKey(refB))
  );

export const getDisplayNameFromProperties = (
  properties: PropertyRecord
): string => {
  if (isString(properties.name) && properties.name) {
    return properties.name;
  }

  if (isString(properties.description) && properties.description) {
    return properties.description;
  }

  return '';
};

export const enrichDirectRelationRef = (
  ref: NodeRef,
  enrichedByKey: Map<string, PropertyRecord>
): NodeRef | EnrichedNodeRef => {
  const enriched = enrichedByKey.get(getNodeRefKey(ref));
  if (!enriched) {
    return ref;
  }
  const name = getDisplayNameFromProperties(enriched);
  return name ? { ...ref, name } : ref;
};

/**
 * Adds a display name to direct-relation values on an instance (single ref or list).
 * The stored value is usually just space + externalId — we look up the related node
 * in enrichedByKey and attach its name so the table/list can show something readable.
 */
export const enrichDirectRelationValue = (
  value: unknown,
  enrichedByKey: Map<string, PropertyRecord>
): unknown => {
  if (Array.isArray(value)) {
    return value.map((item: unknown) =>
      isDirectRelationRef(item)
        ? enrichDirectRelationRef(item, enrichedByKey)
        : item
    );
  }

  if (isDirectRelationRef(value)) {
    return enrichDirectRelationRef(value, enrichedByKey);
  }

  return value;
};

export const mergeDirectRelationEnrichment = <T extends PropertyRecord>(
  originalNodes: T[],
  propertyId: string,
  enrichedNodes: NodeWithProperties[]
): T[] => {
  const enrichedByKey = new Map(
    enrichedNodes.map((node) => [getNodeRefKey(node), node.properties])
  );

  return originalNodes.map(
    (originalNode): T => ({
      ...originalNode,
      [propertyId]: enrichDirectRelationValue(
        originalNode[propertyId],
        enrichedByKey
      ),
    })
  );
};

/**
 * Builds filter-value → display label map from already-enriched observation rows.
 * Reuses names attached by useEnrichDirectRelations so filter options avoid a
 * separate name fetch for direct relations visible in the current table data.
 */
export const buildDirectRelationFilterLabelsFromInstances = (
  instances: PropertyRecord[],
  propertyId: string
): Map<string, string> => {
  const labels = new Map<string, string>();

  instances.forEach((instance) => {
    const value = instance[propertyId];
    const items = Array.isArray(value) ? value : [value];

    items.forEach((item) => {
      if (!isDirectRelationWithOptionalName(item)) {
        return;
      }
      if (!isString(item.name) || !item.name) {
        return;
      }
      labels.set(getDirectRelationFilterValue(item), item.name);
    });
  });

  return labels;
};
